<script setup lang="ts">
import { useAuth } from "@/services/user/Auth0UserProvider"
import i18n from '@/lang/i18n'
import Api from "@/services/api/ApiServiceFabrick"
import { Ref, ref, reactive, onMounted, watch, defineProps } from 'vue'
import subManagementOfficeType from "@/types/subManagementOffice"
import getSubManagementOffice from "@/types/requests/getSubManagementOffice"
import getPagenatedSubManagementOffice from "@/types/responses/getPagenatedSubManagementOffice"
import PaginateWithCount from "@/components/PaginateWithCount.vue"
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
import { useRouter } from 'vue-router'
import SubManagementList from "@/components/SubManagementOfficeList/SubManagementList.vue"
import SerchForm from "@/components/SubManagementOfficeList/SerchForm.vue"
import FileDownloadBtn from "@/components/Files/DownloadButton.vue"
import FileUploadModal from "@/components/Files/UploadModal.vue"
import { SUB_MANAGEMENT_OFFICE_CSV_IMPORT_TYPE, SUB_MANAGEMENT_OFFICE_ICE_EXPORT_TYPE } from "@/services/consts"
import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";

document.title = i18n.global.t('pages.subManagementOffice.title.listTitle')
const { loginWithRedirect } = useAuth()
const changeStatusAuthority: Ref<boolean> = ref(false)
const createAuthority: Ref<boolean> = ref(false)
const viewAuthority: Ref<boolean> = ref(false)
const showableImportCsvButton: Ref<boolean> = ref(false)
const showableExportCsvButton: Ref<boolean> = ref(false)
const currentPage: Ref<number> = ref(1)
const totalPage: Ref<number> = ref(0)
const itemsPerPage: Ref<number> = ref(50)
const allElementsCount: Ref<number> = ref(0)
const isLoading: Ref<boolean> = ref(false)
const updatingStatus: Ref<boolean> = ref(false)
const subManagementOffices: Ref<subManagementOfficeType[]> = ref([])
const errors: Ref<any> = ref({})
const router = useRouter()
const windowInnerWidth = ref(window.innerWidth);
const isManagementOffice: Ref<boolean> = ref(false);
const searchParams: getSubManagementOffice = reactive({
  page: 1,
  sort: "id",
  sort_type: "ASC",
  status: 'only-active',
  search: "",
});
const csvModalState: Ref<boolean> = ref(false);

window.addEventListener('resize', handleResize);

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => {value: any}
}>()

onMounted(() => {
  getAuthorityList()
  getSelf()
  const savedSearchParams = loadDataFromSessionStorage('subManagementOfficeSearchParams')
  if (savedSearchParams) {
    searchParams.search = savedSearchParams.search
    searchParams.status = savedSearchParams.status
  }
  getSubManagementOffices()
})

async function getAuthorityList(isError = false) {
  if (isError) {
    await AuthorityCacheApi.reflesh()
  }
  const authorityList = await AuthorityCacheApi.getWithParse()
  createAuthority.value = authorityList["octlink.submanagement_office.create"]
  changeStatusAuthority.value = authorityList["octlink.submanagement_office.delete"]
  viewAuthority.value = authorityList["octlink.submanagement_office.read"]
  showableImportCsvButton.value = authorityList["octlink.submanagement_office.input"]
  showableExportCsvButton.value = authorityList["octlink.submanagement_office.output"]
}

function getSelf() {
  Api().getSelfAccount().then((data) => {
    isManagementOffice.value = (data.login_organization_type == 'App\\Models\\ManagementOffice')
  }).catch((error) => {
    return errorHandle(error.response)
  })
}

function getSubManagementOffices() {
  isLoading.value = true
  Api().searchSubManagementOffice(searchParams).then((data: getPagenatedSubManagementOffice) => {
    subManagementOffices.value = data.data
    currentPage.value = data.current_page
    totalPage.value = data.total_page
    allElementsCount.value = data.total
    itemsPerPage.value = data.per_page
    isLoading.value = false
  }).catch((error: any) => {
    return errorHandle(error.response)
  })
}

function searchList (form: getSubManagementOffice) {
  searchParams.page = 1
  searchParams.status = form?.status
  searchParams.search = form?.search
  saveDataToSessionStorage('subManagementOfficeSearchParams', searchParams)
  getSubManagementOffices()
} 

function changeSortOrder (sortItem: string, sortType: string) {
  searchParams.sort = sortItem
  searchParams.sort_type = sortType
  if (currentPage.value == 1) {
    getSubManagementOffices()
  } else {
    currentPage.value = 1;
  }
}

function updateStatus (params: subManagementOfficeType) {
  updatingStatus.value = true
  Api().updateSubManagementOfficeStatus(params.id, params).then(() => {
    props.showFlash(i18n.global.t('pages.common.messages.changeStatusSuccess'), "success", true)
    updatingStatus.value = false
    getSubManagementOffices()
  }).catch((error) => {
    if (error.response.status == 403) {
      getAuthorityList(true)
    }
    return errorHandle(error.response)
  })
}

function moveToForm () {
  router.push('/sub-management-offices/form/')
}

function errorHandle (res: any) {
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      isLoading.value = false
      break
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", true)
      isLoading.value = false
      break
    case 422:
      errors.value = res.data.errors
  }
  return updatingStatus.value = false
}

function handleResize() {
  windowInnerWidth.value = window.innerWidth;
}

function inactivateModalState(isReacquireVehicle: boolean) {
  csvModalState.value = false;
  if (isReacquireVehicle) {
    searchParams.page = 1
    getSubManagementOffices();
  }
}

function clearSubManagementOfficeSearchParams() {
  clearSessionStorage('subManagementOfficeSearchParams')
}

watch(currentPage, () => {
  searchParams.page = currentPage.value
  getSubManagementOffices()
})
</script>

<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.subManagementOffice.title.listTitle') }}
        </h4>
      </div>
      <div class="d-flex justify-end mb-5">
        <div class="btn-area">
          <v-btn
            v-if="viewAuthority && createAuthority && isManagementOffice"
            class="exec-btn mt-0"
            @click="moveToForm"
          >
            {{ i18n.global.t('pages.common.button.newCreate') }}
          </v-btn>
          <FileDownloadBtn
            :showable="showableExportCsvButton"
            btnClass="exec-btn mt-0 ml-1"
            :btnName="i18n.global.t('pages.common.button.csvExport')"
            :type="SUB_MANAGEMENT_OFFICE_ICE_EXPORT_TYPE"
            :filterItems="searchParams"
          />
          <v-btn v-show="showableImportCsvButton" class="exec-btn mt-0 ml-1" @click="csvModalState = true">
            {{ i18n.global.t('pages.common.button.csvImport') }}
          </v-btn>
        </div>
      </div>
      <div>
        <SubManagementList
          class="mb-2"
          :subManagementOffices="subManagementOffices"
          :isLoading="isLoading"
          :changeStatusAuthority="changeStatusAuthority"
          :updatingStatus="updatingStatus"
          @changeStatus="updateStatus"
          @changeSortOrder="changeSortOrder"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
        <hr />
      </div>
    </div>

    <SerchForm
      :isLoading="isLoading"
      @search="searchList"
      @clear="clearSubManagementOfficeSearchParams"
    />
    <FileUploadModal
      :modalState="csvModalState"
      :title="i18n.global.t('pages.subManagementOffice.list.csvImportModalTitle')"
      uploadBtnClass='exec-btn mt-0 ml-1'
      closeBtnClass='mt-0'
      :type="SUB_MANAGEMENT_OFFICE_CSV_IMPORT_TYPE"
      templateCsvFileType="submanagement_offices"
      @closeModal="inactivateModalState"
    />
  </div>
</template>
